// src/admin/pages/UsersManagementPage.js
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  RefreshCw, 
  Search, 
  Calendar,
  Shield,
  Download,
  Eye,
  X,
  Edit,
  Save,
  Plus,
  Trash2,
  TrendingUp,
  Activity,
  BarChart3,
  UserPlus,
  UserCheck
} from 'lucide-react';
import { supabase } from '../../config';
import ApiService from '../../services/apiService';
import { getUserDisplayName } from '../../services/utilityService';

const UsersManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    superAdmins: 0
  });
  const [analytics, setAnalytics] = useState({
    userStats: null,
    activityStats: null,
    pageAnalytics: null,
    userJourney: null
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch users with product roles
      const { data: usersData, error: usersError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (usersError) {
        console.error('Supabase error fetching users:', usersError);
        throw usersError;
      }

      // Fetch all product roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_product_roles')
        .select('user_id, product_name, role');

      if (rolesError) {
        console.error('Error fetching product roles:', rolesError);
      }

      // Organize roles by user_id
      const rolesByUser = {};
      (rolesData || []).forEach(role => {
        if (!rolesByUser[role.user_id]) {
          rolesByUser[role.user_id] = [];
        }
        rolesByUser[role.user_id].push({
          product: role.product_name,
          role: role.role
        });
      });

      // Merge users with their roles
      // Note: email may not be populated in user_profiles as it's primarily stored in auth.users
      const enrichedUsers = (usersData || []).map(user => ({
        ...user,
        product_roles: rolesByUser[user.id] || [],
        // Fallback: use username if email is not set
        display_email: user.email || user.username || 'No email'
      }));

      setUsers(enrichedUsers);
      
      // Calculate stats
      const superAdminCount = enrichedUsers.filter(u => u.is_super_admin === true).length;
      setStats({
        total: enrichedUsers.length,
        active: enrichedUsers.filter(u => u.is_active !== false).length,
        inactive: enrichedUsers.filter(u => u.is_active === false).length,
        superAdmins: superAdminCount
      });

      console.log(`Loaded ${enrichedUsers.length} users (${superAdminCount} super admins) with product roles`);
      console.log('Sample user data:', enrichedUsers[0]); // Debug: show what fields we have
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error loading users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const [userStatsResponse, activityStatsResponse, pageAnalyticsResponse, userJourneyResponse] = await Promise.all([
        ApiService.fetchUserAnalytics(),
        ApiService.fetchActivityAnalytics(),
        ApiService.fetchPageAnalytics(30),
        ApiService.fetchUserJourney()
      ]);

      setAnalytics({
        userStats: userStatsResponse.success ? userStatsResponse.data : null,
        activityStats: activityStatsResponse.success ? activityStatsResponse.data : null,
        pageAnalytics: pageAnalyticsResponse.success ? pageAnalyticsResponse.data : null,
        userJourney: userJourneyResponse.success ? userJourneyResponse.data : null
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    loadAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return 'N/A';
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true; // Show all users when no search term
    
    const searchLower = searchTerm.toLowerCase();
    const displayName = getUserDisplayName(user, '').toLowerCase();
    return (
      displayName.includes(searchLower) ||
      (user.username?.toLowerCase().includes(searchLower)) ||
      (user.email?.toLowerCase().includes(searchLower)) ||
      (user.id?.toLowerCase().includes(searchLower))
    );
  });

  const handleEditUser = (user) => {
    setEditedUser({
      ...user,
      product_roles: user.product_roles || []
    });
    setEditMode(true);
  };

  const handleAddProductRole = () => {
    setEditedUser({
      ...editedUser,
      product_roles: [
        ...editedUser.product_roles,
        { product: 'equity_manager', role: 'viewer' }
      ]
    });
  };

  const handleRemoveProductRole = (index) => {
    const newRoles = [...editedUser.product_roles];
    newRoles.splice(index, 1);
    setEditedUser({
      ...editedUser,
      product_roles: newRoles
    });
  };

  const handleUpdateProductRole = (index, field, value) => {
    const newRoles = [...editedUser.product_roles];
    newRoles[index][field] = value;
    setEditedUser({
      ...editedUser,
      product_roles: newRoles
    });
  };

  const handleSaveUser = async () => {
    if (!editedUser) return;

    setSaving(true);
    try {
      // Update user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          first_name: editedUser.first_name,
          last_name: editedUser.last_name,
          username: editedUser.username,
          email: editedUser.email,
          is_super_admin: editedUser.is_super_admin,
          is_active: editedUser.is_active,
          phone: editedUser.phone,
          updated_at: new Date().toISOString()
        })
        .eq('id', editedUser.id);

      if (profileError) throw profileError;

      // Delete existing product roles
      const { error: deleteError } = await supabase
        .from('user_product_roles')
        .delete()
        .eq('user_id', editedUser.id);

      if (deleteError) throw deleteError;

      // Insert new product roles
      if (editedUser.product_roles.length > 0) {
        const rolesToInsert = editedUser.product_roles.map(role => ({
          user_id: editedUser.id,
          product_name: role.product,
          role: role.role
        }));

        const { error: insertError } = await supabase
          .from('user_product_roles')
          .insert(rolesToInsert);

        if (insertError) throw insertError;
      }

      alert('User updated successfully!');
      setEditMode(false);
      setEditedUser(null);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const exportUsers = () => {
    const csv = [
      ['ID', 'Name', 'Email', 'Username', 'Created At', 'Active'].join(','),
      ...filteredUsers.map(user => [
        user.id,
        getUserDisplayName(user, ''),
        user.email || user.username || '',
        user.username || '',
        formatDate(user.created_at),
        user.is_active !== false ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">Manage all platform users and permissions</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchUsers}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={exportUsers}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Super Admins</p>
              <p className="text-2xl font-bold text-gray-900">{stats.superAdmins}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inactive Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Overview */}
      {!loadingAnalytics && (
        <div className="space-y-6 mb-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white shadow rounded-lg p-5">
              <div className="flex items-center">
                <Activity className="h-6 w-6 text-green-500" />
                <h3 className="ml-3 text-lg font-medium text-gray-900">Daily Active</h3>
              </div>
              <p className="mt-4 text-3xl font-semibold text-gray-900">{analytics.activityStats?.dailyActive || '0'}</p>
              <p className="text-sm text-gray-500">Users today</p>
            </div>
            <div className="bg-white shadow rounded-lg p-5">
              <div className="flex items-center">
                <TrendingUp className="h-6 w-6 text-purple-500" />
                <h3 className="ml-3 text-lg font-medium text-gray-900">Total Page Views</h3>
              </div>
              <p className="mt-4 text-3xl font-semibold text-gray-900">{analytics.pageAnalytics?.totalPageViews?.toLocaleString() || '0'}</p>
              <p className="text-sm text-gray-500">Last 30 days</p>
            </div>
            <div className="bg-white shadow rounded-lg p-5">
              <div className="flex items-center">
                <BarChart3 className="h-6 w-6 text-orange-500" />
                <h3 className="ml-3 text-lg font-medium text-gray-900">Top Pages</h3>
              </div>
              <p className="mt-4 text-3xl font-semibold text-gray-900">{analytics.pageAnalytics?.topPages?.length || 0}</p>
              <p className="text-sm text-gray-500">Tracked pages</p>
            </div>
            <div className="bg-white shadow rounded-lg p-5">
              <div className="flex items-center">
                <Users className="h-6 w-6 text-blue-500" />
                <h3 className="ml-3 text-lg font-medium text-gray-900">Total Users</h3>
              </div>
              <p className="mt-4 text-3xl font-semibold text-gray-900">{analytics.userStats?.totalUsers?.toLocaleString() || stats.total}</p>
              <p className="text-sm text-gray-500">Registered users</p>
            </div>
          </div>

          {/* User Journey */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Journey</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <UserPlus className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{analytics.userJourney?.signups || 0}</p>
                <p className="text-sm text-gray-600">Signups</p>
              </div>
              <div className="text-center">
                <UserCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{analytics.userJourney?.activations || 0}</p>
                <p className="text-sm text-gray-600">Activations</p>
              </div>
              <div className="text-center">
                <Activity className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{analytics.userJourney?.activeUsers || 0}</p>
                <p className="text-sm text-gray-600">Active Users</p>
              </div>
              <div className="text-center">
                <TrendingUp className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{analytics.pageAnalytics?.totalPageViews || 0}</p>
                <p className="text-sm text-gray-600">Page Views</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center">
          <Search className="h-5 w-5 text-gray-400 mr-3" />
          <input
            type="text"
            placeholder="Search by name, email, or username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-gray-700"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            All Users ({filteredUsers.length})
          </h3>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'No users in the system yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full max-w-full">
            <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Access
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className={`h-10 w-10 rounded-full ${user.is_super_admin ? 'bg-purple-100' : 'bg-blue-100'} flex items-center justify-center`}>
                            <span className={`${user.is_super_admin ? 'text-purple-600' : 'text-blue-600'} font-medium text-sm`}>
                              {(getUserDisplayName(user, '') || user.username || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {getUserDisplayName(user, 'Unnamed User')}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {user.id.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.display_email}
                        {!user.email && user.username && (
                          <span className="text-xs text-amber-600 ml-1">(username)</span>
                        )}
                        {!user.email && !user.username && (
                          <span className="text-xs text-red-600 ml-1">(in auth.users)</span>
                        )}
                      </div>
                      {user.username && user.email && user.email !== user.username && (
                        <div className="text-sm text-gray-500">@{user.username}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_super_admin ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          <Shield className="h-3 w-3 mr-1 inline" />
                          Super Admin
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">Standard User</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {user.product_roles && user.product_roles.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.product_roles.map((pr, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                              {pr.product.replace('_', ' ')}: {pr.role}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No products</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(user.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.is_active !== false 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-green-600 hover:text-green-900 inline-flex items-center mr-3"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editMode && editedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit User</h3>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="max-h-96 overflow-auto space-y-4">
                {/* Basic Info */}
                <div className="border-b pb-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={editedUser.first_name || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, first_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={editedUser.last_name || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, last_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={editedUser.username || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, username: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                        <span className="text-xs text-gray-500 ml-1">(Optional - Auth email in auth.users)</span>
                      </label>
                      <input
                        type="email"
                        value={editedUser.email || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                        placeholder="Optional copy of email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="text"
                        value={editedUser.phone || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Admin Status */}
                <div className="border-b pb-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Permissions</h4>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editedUser.is_super_admin || false}
                        onChange={(e) => setEditedUser({ ...editedUser, is_super_admin: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        <Shield className="h-4 w-4 inline mr-1 text-purple-600" />
                        Super Admin (Full platform access)
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editedUser.is_active !== false}
                        onChange={(e) => setEditedUser({ ...editedUser, is_active: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active User</span>
                    </label>
                  </div>
                </div>

                {/* Product Roles */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">Product Access</h4>
                    <button
                      onClick={handleAddProductRole}
                      className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Product
                    </button>
                  </div>
                  
                  {editedUser.product_roles.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No product access assigned</p>
                  ) : (
                    <div className="space-y-2">
                      {editedUser.product_roles.map((role, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <select
                            value={role.product}
                            onChange={(e) => handleUpdateProductRole(idx, 'product', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="equity_manager">Equity Manager</option>
                            <option value="dataroom">Dataroom</option>
                            <option value="capraise">Cap Raise</option>
                          </select>
                          <select
                            value={role.role}
                            onChange={(e) => handleUpdateProductRole(idx, 'role', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                            <option value="admin">Admin</option>
                            <option value="creator">Creator</option>
                          </select>
                          <button
                            onClick={() => handleRemoveProductRole(idx)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Read-only fields */}
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">
                    <strong>User ID:</strong> {editedUser.id}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    <strong>Created:</strong> {formatDate(editedUser.created_at)}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditedUser(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && !editMode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">User Details</h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">User ID</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md break-all">
                      {selectedUser.id}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {getUserDisplayName(selectedUser, 'Not provided')}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {selectedUser.username || 'Not provided'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {selectedUser.email || 'Not provided'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created At</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {formatDate(selectedUser.created_at)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p className="mt-1 text-sm bg-gray-50 p-3 rounded-md">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        selectedUser.is_active !== false 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedUser.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                </div>

                {selectedUser.updated_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {formatDate(selectedUser.updated_at)}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">All User Data</label>
                  <pre className="mt-1 text-xs text-gray-900 bg-gray-50 p-3 rounded-md overflow-auto max-h-60">
                    {JSON.stringify(selectedUser, null, 2)}
                  </pre>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    const userToEdit = selectedUser;
                    setSelectedUser(null);
                    handleEditUser(userToEdit);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Edit User
                </button>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagementPage;

